import {
  Box,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RepeatIcon from "@mui/icons-material/Repeat";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import RepeatOneIcon from "@mui/icons-material/RepeatOne";
import MovieIcon from "@mui/icons-material/Movie";
import ImageIcon from "@mui/icons-material/Image";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import PercentIcon from "@mui/icons-material/Percent";
import StatCard from "./StatCard";
import helpers from "../../../hooks/HelperFunctions";

const HEAD_STYLES = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.80)",
  fontFamily: "Quicksand",
  letterSpacing: 0.8,
  background: "linear-gradient(135deg, #1C1C2E 0%, #2D1A5E 100%)",
  borderBottom: "none",
  py: 1.75,
};

const POST_TYPE_COLOR = {
  Reels:    { bg: "rgba(255,82,0,0.10)",    color: "#FF5200" },
  reel:     { bg: "rgba(255,82,0,0.10)",    color: "#FF5200" },
  Images:   { bg: "rgba(99,102,241,0.10)",  color: "#4F46E5" },
  post:     { bg: "rgba(99,102,241,0.10)",  color: "#4F46E5" },
  Carousels:{ bg: "rgba(16,185,129,0.10)",  color: "#059669" },
  video:    { bg: "rgba(234,88,12,0.10)",   color: "#EA580C" },
  short:    { bg: "rgba(234,88,12,0.10)",   color: "#EA580C" },
};

function getTypeLabel(mediaType, postType) {
  if (mediaType === "Reels"     || postType === "reel")  return "Reel";
  if (mediaType === "Carousels")                          return "Carousel";
  if (mediaType === "Images"    || postType === "post")  return "Post";
  if (postType === "video")                               return "Video";
  if (postType === "short")                               return "Short";
  return mediaType || postType || "Post";
}

function getTypeStyle(mediaType, postType) {
  return (
    POST_TYPE_COLOR[mediaType] ||
    POST_TYPE_COLOR[postType] ||
    { bg: "rgba(107,114,128,0.10)", color: "#6B7280" }
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const ms = Number(dateStr) < 1e12 ? Number(dateStr) * 1000 : Number(dateStr);
  const d = new Date(ms);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
}

function StatChip({ icon, value, color, prefix = "" }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.35} sx={{ whiteSpace: "nowrap" }}>
      {icon}
      <Typography sx={{ fontSize: 12, fontFamily: "Quicksand", fontWeight: 700, color: color || "#374151" }}>
        {prefix}{helpers.formatNumber(value, false)}
      </Typography>
    </Stack>
  );
}

const FADE_SLIDE = {
  "@keyframes fadeSlideIn": {
    "0%":   { opacity: 0, transform: "translateY(8px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
};

// Always allocate 3px on the left so hover does not shift layout.
// Only the color animates on hover.
const rowSx = (idx) => ({
  ...FADE_SLIDE,
  animation: `fadeSlideIn 0.35s ease ${idx * 0.045}s both`,
  "& td:first-of-type": {
    borderLeft: "3px solid transparent",
    transition: "border-left-color 0.15s ease",
  },
  "&:hover": {
    bgcolor: "#FFF5F0",
    "& td:first-of-type": { borderLeftColor: "#FF5200" },
  },
  transition: "background-color 0.15s ease",
});

function SkeletonRows() {
  return Array.from({ length: 5 }).map((_, i) => (
    <TableRow key={i} sx={{ opacity: 1 - i * 0.12 }}>
      <TableCell><Skeleton variant="text" width={90} height={20} /></TableCell>
      <TableCell><Skeleton variant="rounded" width={64} height={22} sx={{ borderRadius: 3 }} /></TableCell>
      <TableCell>
        <Stack direction="row" spacing={1.5}>
          <Skeleton width={52} height={20} />
          <Skeleton width={52} height={20} />
          <Skeleton width={52} height={20} />
        </Stack>
      </TableCell>
      <TableCell><Skeleton variant="text" width={110} height={20} /></TableCell>
      <TableCell><Skeleton variant="text" width={80} height={20} /></TableCell>
      <TableCell><Skeleton variant="text" width={60} height={20} /></TableCell>
      <TableCell><Skeleton variant="text" width={60} height={20} /></TableCell>
      <TableCell sx={{ textAlign: "center" }}><Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: "10px", mx: "auto" }} /></TableCell>
    </TableRow>
  ));
}

function EmptyState() {
  return (
    <TableRow>
      <TableCell colSpan={8} sx={{ border: "none" }}>
        <Box
          sx={{
            textAlign: "center",
            py: 7,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            ...FADE_SLIDE,
            animation: "fadeSlideIn 0.5s ease both",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(255,82,0,0.08) 0%, rgba(255,140,0,0.05) 100%)",
              border: "2px dashed rgba(255,82,0,0.22)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 30,
              mb: 0.5,
            }}
          >
            🎬
          </Box>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, color: "#1C1C2E", fontSize: 15 }}>
            No report data yet
          </Typography>
          <Typography sx={{ fontFamily: "Quicksand", color: "#6B7280", fontSize: 13, maxWidth: 300 }}>
            Once a report includes this creator, their post performance will appear here.
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
}

const fmtRupee = (n) => {
  if (n == null) return null;
  if (n >= 100)  return `₹${Math.round(n).toLocaleString("en-IN")}`;
  if (n >= 1)    return `₹${n.toFixed(2)}`;
  if (n >= 0.01) return `₹${n.toFixed(3)}`;
  return `₹${n.toFixed(4)}`;
};

function CreatorReportTab({ reportData, isLoading }) {
  const navigate = useNavigate();
  const posts   = reportData?.posts   || [];
  const summary = reportData?.summary || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ mt: 3 }}>
      {/* Summary stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item  xs={12} sm={6} lg={3}>
          <StatCard
            heading="Total Reels / Posts"
            value={isLoading ? "—" : (summary.totalPosts ?? 0)}
            icon={<MovieIcon sx={{ fontSize: 18 }} />}
            accentColor="#FF5200"
          />
        </Grid>
        <Grid item  xs={12} sm={6} lg={3}>
          <StatCard
            heading="Total Likes"
            value={isLoading ? "—" : helpers.formatNumber(summary.totalLikes ?? 0, false)}
            icon={<FavoriteIcon sx={{ fontSize: 18 }} />}
            accentColor="#EF4444"
          />
        </Grid>
        <Grid item  xs={12} sm={6} lg={3}>
          <StatCard
            heading="Total Views"
            value={isLoading ? "—" : `${summary.viewsEstimated ? "~" : ""}${helpers.formatNumber(summary.totalViews ?? 0, false)}`}
            icon={<VisibilityIcon sx={{ fontSize: 18 }} />}
            accentColor="#0EA5E9"
          />
        </Grid>
        <Grid item  xs={12} sm={6} lg={3}>
          <StatCard
            heading="Avg Engagement Rate"
            value={isLoading ? "—" : `${summary.avgEngagementRate ?? "0.00"}%`}
            icon={<PercentIcon sx={{ fontSize: 18 }} />}
            accentColor="#6366F1"
          />
        </Grid>
      </Grid>

      {/* Post table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          border: "1px solid rgba(255,82,0,0.08)",
          overflowX: "auto",
          overflowY:'hidden',
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": {
            height: 6,
          },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 10,
            backgroundColor: "#CBD5E1",
          },
        }}
      >
        <Table stickyHeader sx={{minWidth:1100}}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEAD_STYLES, minWidth: 120 ,fontSize:isMobile ? 10 : 11}}>Published On</TableCell>
              <TableCell sx={{ ...HEAD_STYLES, minWidth: 100 ,fontSize:isMobile ? 10 : 11 }}>Type</TableCell>
              <TableCell sx={{ ...HEAD_STYLES, minWidth: 300,fontSize: isMobile ? 10 : 11 }}>Engagement</TableCell>
              <TableCell sx={{ ...HEAD_STYLES, minWidth: 160 , fontSize: isMobile ? 10 : 11 }}>Report</TableCell>
              <TableCell sx={{ ...HEAD_STYLES, minWidth: 120 , fontSize : isMobile ? 10 : 11 }}>Brand</TableCell>
              <TableCell sx={{ ...HEAD_STYLES, minWidth: 90 , fontSize : isMobile ? 10 : 11 }}>CPE</TableCell>
              <TableCell sx={{ ...HEAD_STYLES, minWidth: 90 , fontSize: isMobile ? 10 : 11 }}>CPV</TableCell>
              <TableCell sx={{ ...HEAD_STYLES, textAlign: "center", minWidth: 90 , fontSize:isMobile ? 10 : 11 }}>Preview</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : posts.length === 0 ? (
              <EmptyState />
            ) : (
              posts.map((post, idx) => {
                const label     = getTypeLabel(post.mediaType, post.postType);
                const chipStyle = getTypeStyle(post.mediaType, post.postType);
                // Use the unique post URL as key — that's how we deduplicated on the backend.
                const rowKey    = post.link || String(idx);
                return (
                  <TableRow key={rowKey} sx={rowSx(idx)}>

                    {/* Published date */}
                    <TableCell
                      sx={{
                        fontFamily: "Quicksand",
                        fontWeight: 700,
                        fontSize: isMobile ? 12:14,
                        color: "#374151",
                        borderBottom: "1px solid rgba(0,0,0,0.04)",
                        py: isMobile ? 1 : 1.75,
                      }}
                    >
                      {formatDate(post.publishedAt)}
                    </TableCell>

                    {/* Post type chip */}
                    <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)", py: isMobile ? 1 : 1.75 }}>
                      <Chip
                        label={label}
                        size="small"
                        sx={{
                          bgcolor: chipStyle.bg,
                          color: chipStyle.color,
                          fontFamily: "Quicksand",
                          fontWeight: 700,
                          fontSize: 11,
                          height: 22,
                          border: `1px solid ${chipStyle.color}33`,
                        }}
                      />
                    </TableCell>

                    {/* Engagement stats */}
                    <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)", py: isMobile ? 1 : 1.75}}>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={0.5} alignItems="center">
                        <StatChip
                          icon={<FavoriteIcon sx={{ fontSize: 15, color: "#EF4444" }} />}
                          value={post.like_count}
                          color="#EF4444"
                        />
                        <StatChip
                          icon={<ChatBubbleIcon sx={{ fontSize: 15, color: "#6366F1" }} />}
                          value={post.comment_count}
                          color="#6366F1"
                        />
                        <StatChip
                          icon={<VisibilityIcon sx={{ fontSize: 15, color: "#0EA5E9" }} />}
                          value={post.views}
                          color="#0EA5E9"
                          prefix={post.viewsEstimated ? "~" : ""}
                        />
                        {post.reshare_count > 0 && (
                          <StatChip
                            icon={<RepeatIcon sx={{ fontSize: 15, color: "#10B981" }} />}
                            value={post.reshare_count}
                            color="#10B981"
                          />
                        )}
                        {post.repost_count > 0 && (
                          <StatChip
                            icon={<RepeatOneIcon sx={{ fontSize: 15, color: "#F59E0B" }} />}
                            value={post.repost_count}
                            color="#F59E0B"
                          />
                        )}
                        {post.save_count > 0 && (
                          <StatChip
                            icon={<BookmarkIcon sx={{ fontSize: 15, color: "#8B5CF6" }} />}
                            value={post.save_count}
                            color="#8B5CF6"
                          />
                        )}
                      </Stack>
                    </TableCell>

                    {/* Report name — clickable */}
                    <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)", py: isMobile ? 1 : 1.75 }}>
                      {post.reportId ? (
                        <Box
                          onClick={() => navigate(`/reports/${post.reportId}/dashboard`)}
                          sx={{
                            display: "inline-flex", alignItems: "center", gap: 0.5,
                            cursor: "pointer",
                            "&:hover .rn-text": { color: "#FF5200", textDecoration: "underline" },
                          }}
                        >
                          <Typography
                            className="rn-text"
                            sx={{
                              fontFamily: "Quicksand", fontWeight: 700, fontSize: 13,
                              color: "#374151", transition: "color 0.15s ease",
                              maxWidth: isMobile ? 90 : 140 , overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}
                          >
                            {post.reportName || "—"}
                          </Typography>
                          <OpenInNewIcon sx={{ fontSize: 11, color: "#FF5200", opacity: 0.7, flexShrink: 0 }} />
                        </Box>
                      ) : (
                        <Typography sx={{ fontFamily: "Quicksand", fontSize: 13, color: "#D1D5DB" }}>—</Typography>
                      )}
                    </TableCell>

                    {/* Brand */}
                    <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)", py: isMobile ? 1 : 1.75}}>
                      {post.brand ? (
                        <Typography sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize:isMobile ? 12 : 14, color: "#4F46E5", maxWidth: isMobile ? 70 : 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {post.brand}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontFamily: "Quicksand", fontSize: 13, color: "#D1D5DB" }}>—</Typography>
                      )}
                    </TableCell>

                    {/* CPE */}
                    <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)", py: isMobile ? 1 : 1.75}}>
                      <Typography sx={{ fontFamily: "Quicksand", fontWeight: 700, fontSize:isMobile ? 12 : 14, color: post.cpe != null ? "#059669" : "#D1D5DB" }}>
                        {fmtRupee(post.cpe) ?? "—"}
                      </Typography>
                    </TableCell>

                    {/* CPV */}
                    <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)", py: isMobile ? 1 : 1.75}}>
                      <Typography sx={{ fontFamily: "Quicksand", fontWeight: 700, fontSize:isMobile ? 12 : 14, color: post.cpv != null ? "#0EA5E9" : "#D1D5DB" }}>
                        {fmtRupee(post.cpv) ?? "—"}
                      </Typography>
                    </TableCell>

                    {/* Thumbnail — rightmost column */}
                    <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.04)", py: 1.5, textAlign: "center" }}>
                      <Tooltip title={post.link ? "Open post" : ""} placement="left">
                        <Box
                          onClick={() => post.link && window.open(post.link, "_blank", "noopener,noreferrer")}
                          sx={{
                            position: "relative",
                            width: isMobile ? 44 : 56,
                            height: isMobile ? 44 : 56,
                            borderRadius: "10px",
                            overflow: "hidden",
                            cursor: post.link ? "pointer" : "default",
                            border: "1.5px solid rgba(0,0,0,0.07)",
                            display: "inline-block",
                            "&:hover .th-overlay": { opacity: 1 },
                          }}
                        >
                          {post.thumbnailUrl ? (
                            <Box
                              component="img"
                              src={post.thumbnailUrl}
                              alt=""
                              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: "100%", height: "100%",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                background: `linear-gradient(135deg, ${chipStyle.bg} 0%, ${chipStyle.color}18 100%)`,
                              }}
                            >
                              {(label === "Reel" || label === "Video" || label === "Short")
                                ? <MovieIcon sx={{ fontSize: 22, color: chipStyle.color, opacity: 0.55 }} />
                                : label === "Carousel"
                                ? <ViewCarouselIcon sx={{ fontSize: 22, color: chipStyle.color, opacity: 0.55 }} />
                                : <ImageIcon sx={{ fontSize: 22, color: chipStyle.color, opacity: 0.55 }} />
                              }
                            </Box>
                          )}
                          {post.link && (
                            <Box
                              className="th-overlay"
                              sx={{
                                position: "absolute", inset: 0,
                                bgcolor: "rgba(0,0,0,0.42)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                opacity: 0,
                                transition: "opacity 0.18s ease",
                              }}
                            >
                              <OpenInNewIcon sx={{ fontSize: 15, color: "#fff" }} />
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default CreatorReportTab;
